import "./CardComponentStyles.scss";
import React, { FC, useCallback, useMemo, useRef, useState } from "react";
import CarCardComponentProps from "./CarCardComponentProps";
import CurrencyParse from "../../../utils/CurrencyParse";
import { Link, useLocation } from "react-router-dom";
import { routes } from "../../routes/RoutesComponent";
import { getUrlCar } from "../../../utils/Contants";
import { Warranty } from "../../assets/warranty";
import { getWarrantyBadge, hasWarrantyPlan } from "../../../utils/warrantyPlan";
import { Arrow } from "../../assets/arrow";
import di from "../../../../di/DependencyInjection";
import GetCarImagesUseCase, { GetCarImagesUseCaseName } from "../../../../domain/use_cases/car/GetCarImagesUseCase";
import { buildSrcSet, imageKey, variantsFor } from "../../../../utils/image";
import { trackEvent } from "../../../../lib/analytics";
import FavoriteButton from "../favoriteButton/FavoriteButton";

// Cantidad maxima de   puntos visibles en el indicador del carousel.
const MAX_DOTS = 5;

// El ancho de despliegue de la card: ~330px en movil, 291px en >=1024px (ver CardComponentStyles.scss).
// Con este `sizes` el navegador elige la variante segun ancho x densidad de pantalla.
const CARD_IMAGE_SIZES = "(min-width: 1024px) 291px, 330px";

const mergeImages = (current: string[], incoming: string[]): string[] => {
    const keys = new Set(current.map(imageKey));
    const newImages = incoming.filter((image) => {
        const key = imageKey(image);
        if (!image || keys.has(key)) return false;
        keys.add(key);
        return true;
    });
    return newImages.length > 0 ? [...current, ...newImages] : current;
};

const CarCardComponent: FC<CarCardComponentProps> = ({
    car,
    accountUser,
    rutaDetalle,
    origen = "catalogo",
    posicion = 0,
    pagina = 1,
}) => {
    const [imgLoaded, setImgLoaded] = useState(false);
    const [isOrangeFestCar, setIsOrangeFestCar] = React.useState<boolean>(false);
    const location = useLocation();

    const initialImages = useMemo(() => {
        const previews = car.previewImages?.length ? car.previewImages : (car.images ?? []);
        return mergeImages([], [car.photoUrl, ...previews].filter(Boolean));
    }, [car]);

    const [images, setImages] = useState<string[]>(initialImages);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const carouselRef = useRef<HTMLDivElement>(null);
    const allImagesRequestedRef = useRef<boolean>(false);
    const settleTimerRef = useRef<any>(null);
    const renderedCarIdRef = useRef<string>(car.id);

    // Los listados reutilizan la instancia del card (keys por indice), asi que al cambiar
    // de vehiculo reiniciamos el carousel de forma sincrona (antes de pintar) para que el
    // DOM ya tenga las imagenes y los clones del loop del carro nuevo.
    if (renderedCarIdRef.current !== car.id) {
        renderedCarIdRef.current = car.id;
        setImages(initialImages);
        setActiveIndex(0);
        allImagesRequestedRef.current = false;
    }

    const hasCarousel = images.length > 1;

    // Loop infinito: clonamos la ultima imagen al inicio y la primera al final. Al pasar
    // del ultimo al primero (o viceversa) el desplazamiento se ve continuo y luego el scroll
    // se reposiciona sin animacion sobre la imagen real correspondiente.
    const slides = hasCarousel ? [images[images.length - 1], ...images, images[0]] : images;
    const firstRealIndex = hasCarousel ? 1 : 0;

    // Deja el scroll sobre la primera imagen real (posicion siguiente al clon inicial).
    React.useLayoutEffect(() => {
        const carousel = carouselRef.current;
        if (!carousel) return;
        carousel.scrollLeft = hasCarousel ? carousel.clientWidth : 0;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [car.id]);

    React.useEffect(() => () => settleTimerRef.current && clearTimeout(settleTimerRef.current), []);

    // Se piden el resto de fotos apenas el usuario intenta pasar a la siguiente imagen
    // (click en las flechas en web, swipe en mobile) y se concatenan al carousel.
    const loadAllImages = useCallback(async () => {
        if (allImagesRequestedRef.current) return;
        allImagesRequestedRef.current = true;
        const allImages = await di.get<GetCarImagesUseCase>(GetCarImagesUseCaseName).call(car.id);
        if (!allImages.length) return;
        setImages((prev) => mergeImages(prev, allImages));
    }, [car.id]);

    const goStep = (event: React.MouseEvent, direction: number) => {
        // Las flechas mueven el carousel; nunca navegan al detalle del vehiculo.
        event.preventDefault();
        event.stopPropagation();
        loadAllImages();
        const carousel = carouselRef.current;
        if (!carousel || !carousel.clientWidth) return;
        const current = Math.round(carousel.scrollLeft / carousel.clientWidth);
        carousel.scrollTo({ left: carousel.clientWidth * (current + direction), behavior: "smooth" });
    };

    const onCarouselScroll = () => {
        const carousel = carouselRef.current;
        if (!carousel || !carousel.clientWidth) return;
        const count = carousel.children.length; // incluye los clones del loop
        const domIndex = Math.round(carousel.scrollLeft / carousel.clientWidth);

        // Traducimos la posicion (con clones) al indice real para los puntos indicadores.
        const logicalIndex =
            count < 3 ? domIndex : domIndex === 0 ? count - 3 : domIndex === count - 1 ? 0 : domIndex - 1;
        setActiveIndex(Math.max(0, logicalIndex));

        // Cuando el scroll se detiene sobre un clon saltamos, sin animacion, a su imagen real.
        if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
        settleTimerRef.current = setTimeout(() => {
            const el = carouselRef.current;
            if (!el || !el.clientWidth) return;
            const total = el.children.length;
            if (total < 3) return;
            const idx = Math.round(el.scrollLeft / el.clientWidth);
            if (idx === 0) el.scrollLeft = el.clientWidth * (total - 2);
            else if (idx === total - 1) el.scrollLeft = el.clientWidth;
        }, 130);
    };

    const dotsStart = Math.max(0, Math.min(activeIndex - Math.floor(MAX_DOTS / 2), images.length - MAX_DOTS));
    const visibleDots = images.slice(dotsStart, dotsStart + MAX_DOTS);

    React.useEffect(() => {
        const carId = parseInt(car?.id);
        if (
            carId === 591 ||
            carId === 561 ||
            carId === 531 ||
            carId === 613 ||
            carId === 582 ||
            carId === 398 ||
            carId === 532 ||
            carId === 517 ||
            carId === 539 ||
            carId === 503
        ) {
            setIsOrangeFestCar(true);
        }
    }, []);

    const warrantyBadge = getWarrantyBadge(car);
    const hasWarranty = hasWarrantyPlan(car);
    const isOneYear = warrantyBadge.variant === "oneYear";
    const hasWarranry = hasWarranty || car.garantia7dias;

    const destino = `${
        accountUser
            ? routes.detailedCarUser.relativePath + "/" + car.id
            : (rutaDetalle ?? routes.detailedCar.relativePath) + "/" + getUrlCar(car)
    }`;
    const estado = { from: `${location.pathname}${location.search}`, origen };

    return (
        <Link
            to={destino}
            // Guardamos de qué página se abrió el detalle (incluye los filtros en la
            // query string) para que el botón "Volver" del detalle sepa a dónde regresar.
            // `origen` viaja por el mismo canal para que `vehiculo_visto` sepa desde
            // qué superficie llegó la visita.
            state={estado}
            onClick={(e) => {
                trackEvent("vehiculo_card_abierto", {
                    vehiculo_id: String(car.id),
                    origen,
                    posicion,
                    pagina,
                    marca: car.brand?.name?.toLowerCase(),
                    precio: car.price,
                });
            }}
            className="w-100 card car_card_component pb-2"
        >
            <div style={{ position: "relative" }}>
                {!imgLoaded && <div className="img_car_skeleton" />}
                <div
                    className="car_card_carousel"
                    ref={carouselRef}
                    onScroll={onCarouselScroll}
                    onTouchMove={loadAllImages}
                >
                    {slides.map((image, index) => {
                        // srcSet solo si el backend ya genero variantes para esta imagen;
                        // si no, cae al `src` normal (comportamiento actual).
                        const carSrcSet = buildSrcSet(variantsFor(car.imageSrcsetByKey, image));
                        return (
                            <img
                                key={`${car.id}-slide-${index}`}
                                src={image}
                                srcSet={carSrcSet}
                                sizes={carSrcSet ? CARD_IMAGE_SIZES : undefined}
                                alt={`${car.name} ${car.brand.name} ${car.type.name}`}
                                title={car.name}
                                className="img-fluid img_car"
                                width={300}
                                height={200}
                                loading="lazy"
                                onLoad={index === firstRealIndex ? () => setImgLoaded(true) : undefined}
                                style={
                                    index === firstRealIndex
                                        ? { opacity: imgLoaded ? 1 : 0, transition: "opacity 0.4s ease-out" }
                                        : undefined
                                }
                            />
                        );
                    })}
                </div>
                {hasCarousel && (
                    <>
                        {/* Las flechas mueven el carousel (loop infinito) y nunca navegan al
                            detalle, para que un click accidental no abra el vehiculo. */}
                        <button
                            type="button"
                            aria-label="Imagen anterior"
                            className="carousel_arrow carousel_arrow_prev"
                            onClick={(event) => goStep(event, -1)}
                        >
                            <Arrow />
                        </button>
                        <button
                            type="button"
                            aria-label="Siguiente imagen"
                            className="carousel_arrow carousel_arrow_next"
                            onClick={(event) => goStep(event, 1)}
                        >
                            <Arrow />
                        </button>
                        <div
                            className={`carousel_dots ${
                                car?.tag?.name == "Vehículo por ingresar" ? "carousel_dots_raised" : ""
                            }`}
                        >
                            {visibleDots.map((image, index) => (
                                <span
                                    key={`${car.id}-dot-${dotsStart + index}`}
                                    className={`carousel_dot ${dotsStart + index === activeIndex ? "active" : ""}`}
                                />
                            ))}
                        </div>
                    </>
                )}
                {!accountUser && (
                    <FavoriteButton
                        car={car}
                        origen={origen === "favoritos" ? "favoritos" : "tarjeta_vehiculo"}
                        style={{ position: "absolute", right: 10, bottom: 10 }}
                    />
                )}
                {/* {isOrangeFestCar && ( */}
                {car?.tag?.name == "Vehículo por ingresar" && (
                    <div
                        style={{
                            width: "100%",
                            height: "26px",
                            backgroundColor: "#000",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            position: "absolute",
                            bottom: 0,
                        }}
                    >
                        <div style={{ color: "white" }}>Estamos preparando el vehículo para ti</div>
                        {/* <img
                    src="/assets/orange-fest.png"
                    style={{
                        width: "100px",
                        height: "16px",
                    }}
                /> */}
                    </div>
                )}
            </div>

            {/* )} */}
            <div className="card-body d-flex flex-column justify-content-evenly">
                {car.tag && car.tag?.id != "6" && (
                    <>
                        {car.tag.name != "Vehículo por ingresar" && car.tag.name != "Black friday %" ? (
                            <>
                                <div
                                    // className="tag-2"
                                    style={{
                                        backgroundColor: car.tag.color,
                                        color:
                                            car.tag.color === "#2E42A1" || car.tag.color == "#4285F4"
                                                ? "#fff"
                                                : car.tag.name === "Fuera de Estandar"
                                                  ? "#fff"
                                                  : car.tag.color === "#000"
                                                    ? "#FF7300"
                                                    : "",
                                        position: "absolute",
                                        top: 0,
                                        padding: "0.8em 0.8em",
                                        ...(!hasWarranry
                                            ? {
                                                  right: 0,
                                                  borderBottomLeftRadius: "0.5rem",
                                              }
                                            : { left: 0, borderBottomRightRadius: "0.5rem" }),
                                    }}
                                >
                                    {car.tag.name}
                                </div>
                            </>
                        ) : (
                            <div
                                // className="tag-2"
                                style={{
                                    backgroundColor: car.tag.color === "#000" ? "#000" : "#E49B0F",
                                    color: car.tag.color === "#000" ? "#FF7300" : "#fff",

                                    position: "absolute",
                                    top: 0,
                                    ...(!hasWarranty && car.tag.name != "Black friday %"
                                        ? {
                                              right: 0,
                                              borderBottomLeftRadius: "0.5rem",
                                          }
                                        : { left: 0, borderBottomRightRadius: "0.5rem" }),
                                    padding: "0.8em 0.8em",
                                }}
                            >
                                {car.tag.name}
                            </div>
                        )}
                    </>
                )}
                {hasWarranty && (
                    <div className="" style={{ position: "absolute", top: 6, right: 0 }}>
                        <div
                            style={{
                                display: !isOneYear ? "none" : "block",
                                top: 0,
                                position: "relative",
                                right: 120,
                                zIndex: 200,
                            }}
                        >
                            <Warranty fill={warrantyBadge.iconFill} warranty3={isOneYear} />{" "}
                        </div>
                        <div
                            className={`btn btn_orange view-btn  d-flex justify-content-center align-items-center ${warrantyBadge.cardClassName}`}
                        >
                            <div style={{ display: isOneYear ? "none" : "block" }}>
                                <Warranty fill={warrantyBadge.iconFill} warranty3={isOneYear} />{" "}
                            </div>
                            {warrantyBadge.label}
                        </div>
                    </div>
                )}
                <div className="d-flex w-100 align-items-center">
                    <div className="flex-grow overflow-hidden">
                        <h5 className="">{car.name}</h5>
                    </div>
                </div>
                <div className="w-100 text_bold mt-1" style={{ color: "#666C89" }}>
                    {car.type.name}
                </div>

                <div className="w-100 row mt-3" style={{ fontSize: ".9em", margin: "0 auto", rowGap: "8px" }}>
                    <div className="col-6 d-flex align-items-center text_gray p-0">
                        <img
                            src="/assets/icons/calendar.svg"
                            alt={car?.name}
                            title={car?.name}
                            className="img-fluid img_icon me-2"
                            width={24}
                            height={24}
                            loading="lazy"
                        />
                        <span>{car.year}</span>
                    </div>
                    <div className="col-6 d-flex align-items-center text_gray p-0">
                        <img
                            src="/assets/icons/odometer.svg"
                            alt={car?.name}
                            title={car?.name}
                            className="img-fluid img_icon me-2"
                            width={24}
                            height={24}
                            loading="lazy"
                        />
                        <span className="text_nowrap">{CurrencyParse.toCop(car.odometer)?.replace("$", "")} Km.</span>
                    </div>
                    <div className="col-6 d-flex align-items-center text_gray p-0">
                        <img
                            src="/assets/icons/transmission.svg"
                            alt={car?.name}
                            title={car?.name}
                            className="img-fluid img_icon me-1"
                            width={24}
                            height={24}
                            loading="lazy"
                        />
                        <span className="text_nowrap">{car.transmission}</span>
                    </div>
                    <div className="col-6 d-flex align-items-center text_gray p-0">
                        <img
                            src="/assets/icons/location.svg"
                            alt={car?.name}
                            title={car?.name}
                            className="img-fluid img_icon me-1"
                            width={24}
                            height={24}
                            loading="lazy"
                        />
                        <span className="text_nowrap">{car.sede_car?.name ?? "Bogotá"}</span>
                    </div>
                </div>
                <div
                    className={`w-100 d-flex align-items-center justfy-content-between ${
                        car.discount_price ? "mt-2" : "mt-2"
                    }`}
                >
                    <div className="flex-grow-1 text_ellipsis">
                        <h5 className={`text_bold card-price`}>
                            {CurrencyParse.toCop(
                                car.discount_price && car.discount_price !== 0 ? car.discount_price : car.price,
                            )}
                        </h5>
                        <span className="text_bold text_gray text_through">
                            {car.discount_price && car.discount_price !== 0 && CurrencyParse.toCop(car.price)}
                        </span>
                    </div>
                    <div className="ms-3 px-4 btn btn_orange view-btn">VER</div>
                </div>
                <div className="d-flex align-items-center text_gray mt-3">
                    <img
                        src="/assets/icons/eye.png"
                        alt={car?.name}
                        title={car?.name}
                        className="img-fluid img_icon me-2"
                        width={20}
                        height={20}
                        loading="lazy"
                    />
                    <span>{car.quantityPersons} Personas han visto este vehículo</span>
                </div>
                {/* <div className="col-12 my-3 position-relative">
                    <span className="warranty_tool_tip position-absolute bg-primary z-10" style={{ display: "block" }}>
                        {car.quantityPersons} personas han {"\n"} visto este vehiculo
                    </span>
                </div> */}
            </div>
        </Link>
    );
};

export default CarCardComponent;
